import React, { useState, useRef, useCallback, useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import Webcam from 'react-webcam';
import { Buffer } from 'buffer';
import { UploadPhoto } from '../../s3Func/s3Upload';
import PhotoTimeCamera from '../../components/World/PhotoTime/PhotoTimeCamera';
import {
  getContentTypeFromMediaKey,
  getFileNameFromMediaKey,
  getMediaTypeFromMediaKey,
} from '../../services/RedoService';
import countdownSound from '../../PhotoTime/count_down_beep.m4a';
import loading from '../../loading-gif-png-5.gif';
import '../../Celebrate/Recorder.css';

window.Buffer = Buffer;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const iconStyle = {
  width: '75px',
  height: '75px',
  cursor: 'pointer',
};

export default function RedoRecorder({ redo, background, onComplete }) {
  const { login } = useSelector((state) => state.loginReducer, shallowEqual);
  const fileName = getFileNameFromMediaKey(redo?.media_key);
  const mediaType = getMediaTypeFromMediaKey(redo?.media_key);
  const contentType = getContentTypeFromMediaKey(redo?.media_key);

  const generateImageUrl = (baseUrl) =>
    login.cloudFront + baseUrl + login.cookie;

  const confirmIcon = generateImageUrl(
    'images/Module1Images/check_button_small.png'
  );
  const cancelIcon = generateImageUrl('images/Module1Images/x_button.png');
  const playButton = generateImageUrl('images/Images/Video play.png');
  const pauseButton = generateImageUrl('images/Images/pause.png');

  const recordBtn = useRef();
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const timer = useRef();

  const [capturing, setCapturing] = useState(false);
  const [notCapturing, setNotCapturing] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [dataUri, setDataUri] = useState('');
  const [photoUri, setPhotoUri] = useState('');
  const [contin, setContin] = useState(false);
  const [pause, setPause] = useState(false);
  const [playBtn, setPlayBtn] = useState(false);
  const [waiting, setWaiting] = useState(false);

  const cdSound = new Audio(countdownSound);

  const videoConstraints = { width: 391, height: 580 };

  const isIOS = () =>
    [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod',
    ].includes(navigator.platform) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

  const isLandscape = () => window.matchMedia('(orientation: landscape)').matches;

  if (isIOS()) {
    const DefaultMediaRecorder = MediaRecorder;
    const kb = 8 * 1024;
    const preferredBitRatePerSecond = 100 * kb;
    MediaRecorder = class extends DefaultMediaRecorder {
      constructor(stream, options) {
        super(stream, {
          ...options,
          audioBitsPerSecond: preferredBitRatePerSecond,
          videoBitsPerSecond: preferredBitRatePerSecond,
        });
      }
    };
  }

  const handleDataAvailable = useCallback(({ data }) => {
    if (data.size > 0) {
      setRecordedChunks((prev) => prev.concat(data));
    }
  }, []);

  const uploadRecording = async (uri) => {
    setWaiting(true);
    const result = await UploadPhoto(
      uri,
      'userfiles',
      fileName,
      contentType,
      login.studentid
    );

    if (result === 200) {
      onComplete();
      return;
    }

    setWaiting(false);
    window.alert(
      'Whoops, failed to upload. It looks like the internet connection was lost. Please try again after login.'
    );
  };

  const handleStopCaptureClick = () => {
    mediaRecorderRef.current.stop();
    mediaRecorderRef.current.addEventListener(
      'dataavailable',
      handleDataAvailable
    );
    setCapturing(false);
    setNotCapturing(false);
    setContin(true);
    clearTimeout(timer.current);
  };

  const handleStartCaptureClick = async () => {
    setCapturing(false);
    setNotCapturing(true);
    await sleep(10);
    recordBtn.current.disabled = true;
    recordBtn.current.style.opacity = 0;
    mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
      mimeType: isIOS() ? 'video/mp4' : 'video/webm',
    });
    mediaRecorderRef.current.start();
    timer.current = setTimeout(() => handleStopCaptureClick(), 60000);
    await sleep(2000);
    recordBtn.current.disabled = false;
    recordBtn.current.style.opacity = 1;
  };

  const uploadVid = () => {
    if (recordedChunks.length) {
      const newBlob = new Blob(recordedChunks, {
        type: 'video/mp4',
      });
      const url = URL.createObjectURL(newBlob);
      setRecordedChunks([]);
      return url;
    }
    return null;
  };

  const resetVideoRecording = () => {
    setDataUri('');
    setContin(false);
    setNotCapturing(false);
    setPlayBtn(false);
    setPause(false);
    sleep(2000).then(() => setCapturing(true));
  };

  useEffect(() => {
    if (mediaType === 'video') {
      sleep(2000).then(() => setCapturing(true));
    }
  }, [mediaType]);

  if (mediaType === 'unknown') {
    return (
      <div className='redo-page'>
        <p>This recording type is not supported for redo yet.</p>
        <Button onClick={onComplete}>Continue</Button>
      </div>
    );
  }

  if (waiting) {
    return (
      <div id='recordpage' style={{ backgroundImage: `url(${background})` }}>
        <img id='loading' alt='loading' src={loading} />
      </div>
    );
  }

  if (mediaType === 'photo') {
    return (
      <PhotoTimeCamera
        text={redo?.prompt}
        background={background}
        dataUri={photoUri}
        countdown={-1}
        onCameraClick={({ webCamEvent }) => {
          const screenshot = webCamEvent.getScreenshot();
          if (screenshot) {
            setPhotoUri(screenshot);
          }
        }}
        onConfirmPreview={() => uploadRecording(photoUri)}
        onCancelPreview={() => setPhotoUri('')}
      />
    );
  }

  if (dataUri) {
    return (
      <div
        id='recordpage'
        style={{ backgroundImage: `url(${background})` }}
      >
        <div id='border1'>
          <h4 id='cameraText'>Use this video?</h4>
          <video
            id='record-video'
            autoPlay
            src={dataUri}
            onEnded={() => {
              setPause(false);
              setPlayBtn(true);
              const pauseEl = document.getElementById('pauseButton');
              if (pauseEl) {
                pauseEl.src = playButton;
              }
            }}
            onError={() => {
              setPause(false);
              setPlayBtn(true);
              const pauseEl = document.getElementById('pauseButton');
              if (pauseEl) {
                pauseEl.src = playButton;
              }
            }}
            playsInline
          />
          <img
            alt='cancel button'
            className='cancel'
            style={iconStyle}
            src={cancelIcon}
            onClick={resetVideoRecording}
          />
          {playBtn && (
            <img
              alt='play button'
              id='pauseButton'
              style={{
                left: '50%',
                marginLeft: '-37.5px',
                position: 'absolute',
                bottom: '1vh',
                ...iconStyle,
              }}
              src={playButton}
              onClick={() => {
                const video = document.getElementById('record-video');
                if (pause) {
                  video.pause();
                  document.getElementById('pauseButton').src = playButton;
                  setPause(false);
                } else {
                  video.play();
                  document.getElementById('pauseButton').src = pauseButton;
                  setPause(true);
                }
              }}
            />
          )}
          <img
            alt='confirm button'
            className='confirm'
            style={iconStyle}
            src={confirmIcon}
            onClick={() => uploadRecording(dataUri)}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      id='recordpage'
      style={{ backgroundImage: `url(${background})` }}
    >
      <div id='border1'>
        <h4 id='cameraText'>{redo?.prompt}</h4>
        <Webcam
          id='record-video'
          audio={true}
          muted
          videoConstraints={
            isIOS() && !isLandscape()
              ? { height: 391, width: 580 }
              : videoConstraints
          }
          ref={webcamRef}
        />
        {notCapturing && (
          <button
            className='stopButton'
            ref={recordBtn}
            onClick={handleStopCaptureClick}
          />
        )}
        {capturing && (
          <button
            className='recordButton'
            onClick={() => {
              cdSound.play();
              sleep(500).then(() => handleStartCaptureClick());
            }}
          />
        )}
        {contin && (
          <Button
            className='continueButton'
            onClick={() => {
              setDataUri(uploadVid());
            }}
          >
            Continue
          </Button>
        )}
      </div>
    </div>
  );
}
